import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Tooltip,
  Button,
  Input,
  SortDescriptor
} from "@nextui-org/react";
import { useTranslation } from "react-i18next";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";

import { EyeIcon, EditIcon, DeleteIcon, SearchIcon } from "@/components/icons";
import DefaultLayout from "@/layouts/default";
import { booksAPI } from "@/services/api";
import { useAuth } from "@/context/AuthContext";
import DataTable, { Column } from "@/components/DataTable";

interface Book {
  id: string;
  name: string;
  author: string;
  category: string;
  stock: number;
  total_sold?: number;
  price: number;
  clientId: string;
  publisher?: string;
  minStocks?: number;
  price_per_book?: number;
  version?: number;
  total_versions?: number;
  versions?: Array<{
    id: string;
    version: number;
    stock: number;
    price_per_book: number;
  }>;
}

// Custom CSS for low stock row styling
const lowStockStyles = `
  .low-stock-row {
    background-color: #fecaca !important;
    border-left: 4px solid #dc2626 !important;
  }
  .low-stock-row:hover {
    background-color: #fca5a5 !important;
  }
  .low-stock-row td {
    background-color: inherit !important;
  }
`;

const BooksPage: React.FC = () => {
  const [books, setBooks] = useState<Book[]>([]);
  const [filteredBooks, setFilteredBooks] = useState<Book[]>([]);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [isDownloading, setIsDownloading] = useState<boolean>(false);
  const [sortDescriptor, setSortDescriptor] = useState<SortDescriptor>({
    column: "id",
    direction: "descending"
  });
  
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user } = useAuth();

  // Inject custom styles for low stock rows
  useEffect(() => {
    const styleId = "low-stock-styles";
    if (!document.getElementById(styleId)) {
      const styleElement = document.createElement("style");
      styleElement.id = styleId;
      styleElement.innerHTML = lowStockStyles;
      document.head.appendChild(styleElement);

      return () => {
        const existingStyle = document.getElementById(styleId);
        if (existingStyle) {
          document.head.removeChild(existingStyle);
        }
      };
    }
  }, []);

  useEffect(() => {
    if (!user) {
      setError("You must be logged in to view books");
      setLoading(false);

      return;
    }

    const fetchBooks = async () => {
      try {
        setLoading(true);
        setError(null);

        // Get clientId from user object in auth context
        const clientId = user.clientId;

        if (!clientId) {
          setError(t("client_id_not_found"));
          setLoading(false);

          return;
        }

        // Fetch books from API
        const booksData = await booksAPI.getAll(clientId);

        // Ensure all books have proper stock, total_sold, and minStocks values
        const processedBooks = booksData.map((book: any) => ({
          ...book,
          // Make sure stock is present under the right field name
          stock: Number(book.stock) || 0,
          // Make sure total_sold is present
          total_sold: Number(book.total_sold || book.totalSold) || 0,
          // Make sure minStocks is present under the right field name
          minStocks: Number(book.min_stocks || book.minStocks) || 0,
        }));

        // Books are already sorted by ID in descending order from the backend
        setBooks(processedBooks);
        setFilteredBooks(processedBooks);
        setLoading(false);
      } catch (error: any) {
        setError(`Error: ${error.message || "Unknown error occurred"}`);
        setLoading(false);
      }
    };

    fetchBooks();
  }, [t, user]);

  // Filter books based on search query
  const filterBooks = (query: string) => {
    if (!query.trim()) {
      setFilteredBooks(books);
    } else {
      const lowerCaseQuery = query.toLowerCase();
      const filtered = books.filter(
        (book) =>
          book.name.toLowerCase().includes(lowerCaseQuery) ||
          (book.category &&
            book.category.toLowerCase().includes(lowerCaseQuery)) ||
          (book.publisher &&
            book.publisher.toLowerCase().includes(lowerCaseQuery)) ||
          String(book.price).includes(lowerCaseQuery) ||
          String(book.stock).includes(lowerCaseQuery) ||
          String(book.total_sold || 0).includes(lowerCaseQuery) ||
          String((Number(book.stock) || 0) + (Number(book.total_sold) || 0)).includes(lowerCaseQuery),
      );
      setFilteredBooks(filtered);
    }

    // Reset sort descriptor to ensure consistent serial numbering
    setSortDescriptor({
      column: "id",
      direction: "descending"
    });
  };

  // Handle search input change
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;

    setSearchQuery(query);
    filterBooks(query);
  };

  const handleView = (id: string) => {
    navigate(`/book/view/${id}`);
  };

  const handleEdit = (id: string) => {
    navigate(`/bookEdit/${id}`);
  };

  const handleDelete = async (id: string) => {
    const bookToDelete = books.find((book) => book.id === id);

    if (bookToDelete) {
      const confirmMessage = t('Are you sure you want to delete the book "{{name}}"?', {
        name: bookToDelete.name,
        interpolation: { escapeValue: false }
      });
      
      const confirmDelete = window.confirm(confirmMessage);

      if (confirmDelete) {
        try {
          // Add loading state while deleting
          setLoading(true);
          setError(null); // Clear any previous errors
          
          console.log(`Attempting to delete book with ID: ${id}`);
          const response = await booksAPI.delete(id);
          
          console.log("Delete response:", response);
          
          // Set the updated lists after successful deletion
          setBooks((prevBooks) => prevBooks.filter((book) => book.id !== id));
          setFilteredBooks((prevFiltered) => prevFiltered.filter((book) => book.id !== id));
          
          alert(t("Book deleted successfully!"));
          setLoading(false);
        } catch (error: any) {
          console.error("Error deleting book:", error);
          setLoading(false);
          
          let errorMessage = "";
          
          // Try to extract more detailed error information
          if (error.response) {
            // Server responded with an error status
            const serverError = error.response.data?.error || error.response.data?.details;
            errorMessage = serverError || `Server error: ${error.response.status}`;
            console.log("Server error details:", error.response.data);
          } else if (error.request) {
            // Request was made but no response received
            errorMessage = "No response from server. Please check your connection.";
          } else {
            // Something else happened
            errorMessage = error.message || "Unknown error";
          }
          
          setError(t("Failed to delete book: {error}", { error: errorMessage }));
          
          // Also show an alert for immediate feedback
          alert(
            t("Failed to delete book: {error}", {
              error: errorMessage,
            }),
          );
        }
      }
    }
  };

  // Handle sort change
  const handleSortChange = (descriptor: SortDescriptor) => {
    setSortDescriptor(descriptor);
  };

  // Function to determine row styling based on total stock levels
  const getRowClassName = (book: Book) => {
    const currentStock = Number(book.stock) || 0;
    const minStock = Number(book.minStocks) || 0;
    
    // Apply red background if current total stock is at or below minimum stock
    if (currentStock <= minStock && minStock > 0) {
      return "low-stock-row";
    }
    
    return "";
  };

  // Helper function to fix RTL text with brackets/parentheses
  const fixRTLBrackets = (text: string): string => {
    if (!text) return text;
    // Wrap brackets and their content in LTR direction span
    // This ensures brackets display correctly in RTL context
    return text.replace(/\(([^)]+)\)/g, '<span dir="ltr" style="direction: ltr; unicode-bidi: embed;">($1)</span>');
  };

  // Download PDF report function with RTL and Urdu support
  const downloadPDF = async () => {
    if (filteredBooks.length === 0) {
      alert(t("NoDataToDownload", "No books available to download."));
      return;
    }

    setIsDownloading(true);
    setError(null);

    try {
      // Preload and wait for the Urdu font to be ready
      const fontUrl = '/fonts/Mehr_Nastaliq_Web v.2.0.woff';
      const fontName = 'Mehr Nastaliq';
      
      // Check if font is already loaded, if not, load it
      try {
        if (!document.fonts.check(`16px "${fontName}"`)) {
          const fontFace = new FontFace(fontName, `url(${fontUrl})`);
          await fontFace.load();
          document.fonts.add(fontFace);
          // Wait a bit more to ensure font is fully ready for rendering
          await new Promise(resolve => setTimeout(resolve, 300));
        }
      } catch (fontError) {
        console.warn("Font loading error, continuing with fallback:", fontError);
      }

      // Create a temporary container for the PDF content with RTL support
      const pdfContainer = document.createElement("div");
      pdfContainer.style.position = "absolute";
      pdfContainer.style.left = "-9999px";
      pdfContainer.style.top = "0";
      pdfContainer.style.width = "210mm"; // A4 width
      pdfContainer.style.padding = "15mm"; // Reduced padding to give more space
      pdfContainer.style.backgroundColor = "white";
      pdfContainer.style.direction = "rtl";
      pdfContainer.style.fontFamily = `"${fontName}", "Mehr Nastaliq Web", Arial, Tahoma, sans-serif`;
      pdfContainer.style.fontSize = "11px"; // Slightly smaller font
      pdfContainer.style.color = "#000";
      pdfContainer.style.overflowX = "hidden"; // Prevent horizontal scroll
      pdfContainer.style.overflowY = "hidden"; // Prevent vertical scroll
      pdfContainer.style.boxSizing = "border-box"; // Include padding in width calculation
      pdfContainer.dir = "rtl";

      // Add font-face style to the container with absolute URL
      const fontStyle = document.createElement("style");
      const baseUrl = window.location.origin;
      fontStyle.textContent = `
        @font-face {
          font-family: '${fontName}';
          src: url('${baseUrl}${fontUrl}') format('woff');
          font-weight: normal;
          font-style: normal;
          font-display: block;
        }
        @font-face {
          font-family: 'Mehr Nastaliq Web';
          src: url('${baseUrl}${fontUrl}') format('woff');
          font-weight: normal;
          font-style: normal;
          font-display: block;
        }
        * {
          font-family: "${fontName}", "Mehr Nastaliq Web", Arial, Tahoma, sans-serif !important;
          box-sizing: border-box;
        }
        table {
          table-layout: fixed !important;
          width: 100% !important;
          max-width: 100% !important;
        }
        colgroup col:nth-child(1) {
          width: 5% !important;
          min-width: 5% !important;
          max-width: 5% !important;
        }
        colgroup col:nth-child(2) {
          width: 45% !important;
          min-width: 45% !important;
          max-width: 45% !important;
        }
        colgroup col:nth-child(3),
        colgroup col:nth-child(4),
        colgroup col:nth-child(5) {
          width: 15% !important;
          min-width: 15% !important;
          max-width: 15% !important;
        }
        th, td {
          word-wrap: break-word !important;
          overflow-wrap: break-word !important;
        }
        th:nth-child(1), td:nth-child(1) {
          width: 5% !important;
        }
        th:nth-child(2), td:nth-child(2) {
          width: 45% !important;
        }
        th:nth-child(3), td:nth-child(3),
        th:nth-child(4), td:nth-child(4),
        th:nth-child(5), td:nth-child(5) {
          width: 15% !important;
        }
      `;
      pdfContainer.appendChild(fontStyle);

      // Helper function to create table HTML with header
      const createTableHTML = (rows: Book[], startIndex: number) => {
        const fontFamilyStr = `"${fontName}", "Mehr Nastaliq Web", Arial, Tahoma, sans-serif`;
        const headerHTML = `
          <thead>
            <tr style="background-color: #4285c4; color: white;">
              <th style="border: 1px solid #ddd; padding: 6px; text-align: center; font-weight: bold; font-family: ${fontFamilyStr}; word-wrap: break-word; overflow-wrap: break-word;">${t("S.No", "نمبر")}</th>
              <th style="border: 1px solid #ddd; padding: 6px; text-align: right; font-weight: bold; font-family: ${fontFamilyStr}; word-wrap: break-word; overflow-wrap: break-word;">${t("Name", "کتاب کا نام")}</th>
              <th style="border: 1px solid #ddd; padding: 6px; text-align: center; font-weight: bold; font-family: ${fontFamilyStr}; word-wrap: break-word; overflow-wrap: break-word;">${t("Total Books", "کل کتابیں")}</th>
              <th style="border: 1px solid #ddd; padding: 6px; text-align: center; font-weight: bold; font-family: ${fontFamilyStr}; word-wrap: break-word; overflow-wrap: break-word;">${t("Total Sold", "کل فروخت")}</th>
              <th style="border: 1px solid #ddd; padding: 6px; text-align: center; font-weight: bold; font-family: ${fontFamilyStr}; word-wrap: break-word; overflow-wrap: break-word;">${t("Present Stock", "موجودہ اسٹاک")}</th>
            </tr>
          </thead>
        `;

        const rowsHTML = rows
          .map(
            (book, idx) => {
              const totalSold = Number(book.total_sold) || 0;
              const presentStock = Number(book.stock) || 0;
              const totalBooks = totalSold + presentStock;
              const currentStock = Number(book.stock) || 0;
              const minStock = Number(book.minStocks) || 0;
              const isLowStock = currentStock <= minStock && minStock > 0;
              
              // Fix RTL brackets in book name
              const fixedBookName = fixRTLBrackets(book.name);
              
              return `
              <tr style="background-color: ${idx % 2 === 0 ? "#f9f9f9" : "white"}; ${isLowStock ? "background-color: #fecaca !important;" : ""}">
                <td style="border: 1px solid #ddd; padding: 6px; text-align: center; font-family: ${fontFamilyStr}; word-wrap: break-word; overflow-wrap: break-word;">${startIndex + idx + 1}</td>
                <td style="border: 1px solid #ddd; padding: 6px; text-align: right; font-family: ${fontFamilyStr}; word-wrap: break-word; overflow-wrap: break-word; direction: rtl; unicode-bidi: embed;">${fixedBookName}</td>
                <td style="border: 1px solid #ddd; padding: 6px; text-align: center; font-family: ${fontFamilyStr}; word-wrap: break-word; overflow-wrap: break-word;">${totalBooks}</td>
                <td style="border: 1px solid #ddd; padding: 6px; text-align: center; font-family: ${fontFamilyStr}; word-wrap: break-word; overflow-wrap: break-word;">${totalSold}</td>
                <td style="border: 1px solid #ddd; padding: 6px; text-align: center; color: ${isLowStock ? "red" : "green"}; font-family: ${fontFamilyStr}; word-wrap: break-word; overflow-wrap: break-word;">${presentStock}</td>
              </tr>
            `;
            }
          )
          .join("");

        return `
          <table style="width: 100%; max-width: 100%; border-collapse: collapse; direction: rtl; table-layout: fixed;">
            <colgroup>
              <col style="width: 5%;">
              <col style="width: 45%;">
              <col style="width: 15%;">
              <col style="width: 15%;">
              <col style="width: 15%;">
            </colgroup>
            ${headerHTML}
            <tbody>
              ${rowsHTML}
            </tbody>
          </table>
        `;
      };

      // Create header HTML (title, date)
      const now = new Date();
      const fontFamilyStr = `"${fontName}", "Mehr Nastaliq Web", Arial, Tahoma, sans-serif`;
      const headerHTML = `
        <div style="text-align: center; margin-bottom: 30px;">
          <h1 style="font-size: 24px; margin-bottom: 15px; color: #333; font-family: ${fontFamilyStr};">
            ${t("Books", "کتابوں کی فہرست")}
          </h1>
          <p style="font-size: 12px; margin: 5px 0; color: #666; font-family: ${fontFamilyStr};">
            ${t("GeneratedOn", "وقت اجراء رپورٹ")}: ${now.toLocaleDateString("ur-PK")} - ${now.toLocaleTimeString("ur-PK")}
          </p>
          ${searchQuery ? `<p style="font-size: 12px; margin: 5px 0; color: #666; font-family: ${fontFamilyStr};">${t("Filtering by", "فلٹر")}: ${searchQuery}</p>` : ""}
        </div>
      `;

      // Estimate rows per page (conservative estimate to ensure rows don't split)
      const rowsPerPageFirst = 20; // Conservative for first page
      const rowsPerPageSubsequent = 24; // Conservative for subsequent pages

      // Create PDF
      const pdf = new jsPDF("p", "mm", "a4");
      const imgWidth = 210; // A4 width in mm

      // Split data into chunks and create pages
      const totalRows = filteredBooks.length;
      let currentIndex = 0;
      let pageNumber = 0;

      while (currentIndex < totalRows) {
        const rowsPerPage = pageNumber === 0 ? rowsPerPageFirst : rowsPerPageSubsequent;
        const chunk = filteredBooks.slice(currentIndex, currentIndex + rowsPerPage);
        
        // Create page content - header only on first page, table header on every page
        pdfContainer.innerHTML = pageNumber === 0
          ? headerHTML + createTableHTML(chunk, currentIndex)
          : `<div style="margin-bottom: 10px;"></div>` + createTableHTML(chunk, currentIndex); // Small margin for subsequent pages

        document.body.appendChild(pdfContainer);
        
        // Wait longer for font to render properly
        await new Promise((resolve) => setTimeout(resolve, 500));

        const canvas = await html2canvas(pdfContainer, {
          scale: 1.5, // Reduced from 2 to 1.5 for faster rendering while maintaining quality
          useCORS: true,
          logging: false,
          backgroundColor: "#ffffff",
          windowWidth: pdfContainer.scrollWidth,
          windowHeight: pdfContainer.scrollHeight,
          removeContainer: false,
          onclone: (clonedDoc) => {
            // Ensure font is applied in the cloned document
            const clonedContainer = clonedDoc.querySelector('div[dir="rtl"]');
            if (clonedContainer) {
              (clonedContainer as HTMLElement).style.fontFamily = `"${fontName}", "Mehr Nastaliq Web", Arial, Tahoma, sans-serif`;
            }
          },
        });

        document.body.removeChild(pdfContainer);

        // Add to PDF
        if (pageNumber > 0) {
          pdf.addPage();
        }

        const imgHeight = (canvas.height * imgWidth) / canvas.width;
        const imgData = canvas.toDataURL("image/jpeg", 0.92);
        pdf.addImage(imgData, "JPEG", 0, 0, imgWidth, imgHeight);

        currentIndex += rowsPerPage;
        pageNumber++;
      }

      // Generate filename
      const filename = `Books_List_${now.toISOString().split("T")[0]}.pdf`;

      // Save the PDF
      pdf.save(filename);

      console.log(`PDF report downloaded as: ${filename}`);
    } catch (error: any) {
      console.error("PDF generation error:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        "Failed to generate PDF report";
      setError(
        t("ErrorDownloadingReport", "Error downloading report: ") + errorMessage
      );
    } finally {
      setIsDownloading(false);
    }
  };

  // Define columns for DataTable
  const columns: Column<Book>[] = [
    { 
      key: "name", 
      label: t("Name"),
      sortable: true,
      renderCell: (book) => (
        <span className="text-bold text-sm capitalize">{book.name}</span>
      )
    },
    { 
      key: "total_books", 
      label: t("Total Books"),
      sortable: true,
      renderCell: (book) => {
        const totalSold = Number(book.total_sold) || 0;
        const presentStock = Number(book.stock) || 0;
        const totalBooks = totalSold + presentStock;
        return (
          <span className="text-bold text-sm text-600 font-semibold">
            {totalBooks}
          </span>
        );
      }
    },
    { 
      key: "total_sold", 
      label: t("Total Sold"),
      sortable: true,
      renderCell: (book) => {
        const totalSold = Number(book.total_sold) || 0;
        return (
          <span className="text-bold text-sm text-600">
            {totalSold}
          </span>
        );
      }
    },
    { 
      key: "stock", 
      label: t("Present Stock"),
      sortable: true,
      renderCell: (book) => {
        const currentStock = Number(book.stock) || 0;
        const minStock = Number(book.minStocks) || 0;
        const isLowStock = currentStock <= minStock && minStock > 0;
        
        return (
          <span className={`text-bold text-sm ${isLowStock ? 'text-600 font-semibold' : 'text-success'}`}>
            {currentStock}
            {isLowStock && (
              <span className="ml-1 text-xs text-500"></span>
            )}
          </span>
        );
      }
    },
    { 
      key: "actions", 
      label: t("Actions"),
      renderCell: (book) => (
        <div className="relative flex justify-center items-center gap-2">
          <Tooltip content={t("View Book")}>
            <button
              aria-label={t("View Book")}
              className="text-lg text-default-400 cursor-pointer active:opacity-50"
              onClick={() => handleView(book.id)}
            >
              <EyeIcon />
            </button>
          </Tooltip>
          <Tooltip content={t("Edit Book")}>
            <button
              aria-label={t("Edit Book")}
              className="text-lg text-default-400 cursor-pointer active:opacity-50"
              onClick={() => handleEdit(book.id)}
            >
              <EditIcon />
            </button>
          </Tooltip>
          <Tooltip color="danger" content={t("Delete Book")}>
            <button
              aria-label={t("Delete Book")}
              className="text-lg text-danger cursor-pointer active:opacity-50"
              onClick={() => handleDelete(book.id)}
            >
              <DeleteIcon />
            </button>
          </Tooltip>
        </div>
      )
    },
  ];

  return (
    <DefaultLayout>
      <h1 className="text-3xl font-bold text-center mb-8">{t("Books")}</h1>

      {/* Search and Add Book Controls */}
      <div className="flex flex-col md:flex-row items-center justify-between gap-4 px-8 mb-4">
        <div className="flex w-full md:w-auto justify-between md:justify-start gap-2">
          <Button
            className="min-w-[140px]"
            color="primary"
            size="md"
            onClick={() => navigate("/bookAdd")}
          >
            {t("Add Book")}
          </Button>
          <Button
            className="min-w-[140px]"
            color="success"
            variant="bordered"
            size="md"
            onClick={downloadPDF}
            isLoading={isDownloading}
          >
            {t("Download PDF", "ڈاؤن لوڈ PDF")}
          </Button>
        </div>

        <div className="w-full md:max-w-[320px]">
          <Input
            isClearable
            className="w-full"
            placeholder={t("Search books...")}
            size="md"
            startContent={<SearchIcon className="text-default-400" />}
            value={searchQuery}
            onChange={handleSearchChange}
            onClear={() => {
              setSearchQuery("");
              setFilteredBooks(books);
            }}
          />
        </div>
      </div>

      {/* Search active indicator */}
      {searchQuery && (
        <div className="mb-4 p-2 bg-blue-50 rounded text-sm text-blue-700 flex items-center mx-8">
          <svg
            className="h-5 w-5 mr-2"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
            />
          </svg>
          <span>
            {t("Filtering by")}:
            <span className="ml-1 font-medium">{searchQuery}</span>
          </span>
        </div>
      )}

      <section className="row container px-8 flex flex-col items-center justify-center gap-4 py-2">
        {error && (
          <div className="w-full p-4 mb-4 text-white bg-red-500 rounded">
            {error}
          </div>
        )}

        <DataTable
          columns={columns}
          data={filteredBooks}
          isLoading={loading}
          emptyContent={
            searchQuery
              ? t("No books match your search criteria")
              : t("No books found")
          }
          sortDescriptor={sortDescriptor}
          onSortChange={handleSortChange}
          className="w-full"
          addSerialNumbers={true}
          getRowClassName={getRowClassName}
        />
      </section>
    </DefaultLayout>
  );
};

export default BooksPage;
